import { useEffect, useState } from "react";
import { Download, ExternalLink, Loader2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";

const TONES = { paid: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  open: "bg-amber-50 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300", void: "bg-muted text-muted-foreground",
  uncollectible: "bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300" };
const LABELS = { paid: "Paid", open: "Unpaid", void: "Void", uncollectible: "Failed" };
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const money = (n, cur) => n.toLocaleString("en-US", { style: "currency", currency: cur.toUpperCase() });

const Row = ({ inv }) => (
  <li className="flex items-center justify-between gap-3 py-3" data-testid={`invoice-row-${inv.id}`}>
    <div className="min-w-0">
      <p className="text-sm font-medium text-foreground">{fmtDate(inv.created)} · {money(inv.amount, inv.currency)}</p>
      <p className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
        <span className={`rounded px-1.5 py-0.5 font-medium ${TONES[inv.status] || TONES.void}`} data-testid={`invoice-status-${inv.id}`}>{LABELS[inv.status] || inv.status}</span>
        {inv.number && <span className="truncate">{inv.number}</span>}
      </p>
    </div>
    <div className="flex shrink-0 gap-1.5">
      {inv.hosted_url && (
        <a href={inv.hosted_url} target="_blank" rel="noopener noreferrer" aria-label="View invoice" data-testid={`invoice-view-${inv.id}`}
          className="flex h-9 w-9 items-center justify-center rounded-lg border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"><ExternalLink className="h-4 w-4" /></a>
      )}
      {inv.pdf_url && (
        <a href={inv.pdf_url} target="_blank" rel="noopener noreferrer" data-testid={`invoice-pdf-${inv.id}`}
          className="flex h-9 items-center gap-1.5 rounded-lg border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"><Download className="h-4 w-4" />PDF</a>
      )}
    </div>
  </li>
);

export const InvoiceList = () => {
  const [data, setData] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const load = (after) => api.get("/billing/invoices", { params: after ? { starting_after: after } : {} }).then((r) => r.data);

  useEffect(() => { load().then(setData).catch((e) => { toast.error(errorMessage(e)); setData({ items: [], has_more: false }); }); }, []);
  const more = async () => {
    setLoadingMore(true);
    try { const next = await load(data.next); setData({ ...next, items: [...data.items, ...next.items] }); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setLoadingMore(false); }
  };

  return (
    <section className="rounded-2xl border bg-card p-5 shadow-soft sm:p-6" data-testid="invoice-list">
      <h2 className="flex items-center gap-2 text-base font-semibold text-foreground"><Receipt className="h-4 w-4" />Billing history</h2>
      {!data ? <div className="flex h-20 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : data.items.length === 0 ? <p className="mt-2 text-sm text-muted-foreground" data-testid="invoice-empty">No invoices yet.</p>
        : <ul className="mt-2 divide-y">{data.items.map((inv) => <Row key={inv.id} inv={inv} />)}</ul>}
      {data?.has_more && (
        <button type="button" onClick={more} disabled={loadingMore} data-testid="invoice-show-more-button"
          className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-lg border text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50">
          {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}Show more
        </button>
      )}
    </section>
  );
};
